import { create } from 'zustand';
import type {
  CalendarView,
  CalendarFilters,
  CalendarUserPreferences,
} from '@scrambl/shared';

// Calendar state interface
export interface CalendarState {
  // View state
  currentView: CalendarView;
  currentDate: Date;

  // Display options — persisted per user in user_preferences (see
  // useCalendarPreferencesSync), never in localStorage
  showExternalEvents: boolean;
  showTasks: boolean;
  showScheduledTasks: boolean;

  // Planner sidebar — session only, closed by default: the calendar is the
  // default surface and the tray is opt-in via the Plan button
  isPlannerOpen: boolean;

  // Filters
  filters: CalendarFilters;

  // Actions
  setCurrentView: (view: CalendarView) => void;
  setCurrentDate: (date: Date) => void;
  navigateToToday: () => void;
  navigatePrevious: () => void;
  navigateNext: () => void;
  toggleExternalEvents: () => void;
  toggleTasks: () => void;
  toggleScheduledTasks: () => void;
  setShowExternalEvents: (show: boolean) => void;
  setShowTasks: (show: boolean) => void;
  setShowScheduledTasks: (show: boolean) => void;
  togglePlanner: () => void;
  setPlannerOpen: (open: boolean) => void;
  setFilters: (filters: Partial<CalendarFilters>) => void;
  /** Hydrate filters and display toggles from the saved user preference in one update. */
  applyPreferences: (preferences: CalendarUserPreferences) => void;
  setFilter: <K extends keyof CalendarFilters>(
    key: K,
    value: CalendarFilters[K]
  ) => void;
  resetFilters: () => void;
  clearFilter: (key: keyof CalendarFilters) => void;
}

// Default filters
const DEFAULT_FILTERS: CalendarFilters = {
  entityIds: [],
  projectIds: [],
  assigneeIds: [],
  priorities: [],
  statuses: [],
  tagIds: [],
};

// Helper function to add/subtract time based on view
const navigateDate = (
  date: Date,
  view: CalendarView,
  direction: 'prev' | 'next'
): Date => {
  const newDate = new Date(date);
  const delta = direction === 'next' ? 1 : -1;

  switch (view) {
    case 'month':
      newDate.setMonth(newDate.getMonth() + delta);
      break;
    case 'week':
      newDate.setDate(newDate.getDate() + delta * 7);
      break;
    case 'day':
      newDate.setDate(newDate.getDate() + delta);
      break;
    case 'agenda':
      // Agenda lists a month (listMonth), so it pages by month like the grid
      newDate.setMonth(newDate.getMonth() + delta);
      break;
  }

  return newDate;
};

// Create the calendar store
export const useCalendarStore = create<CalendarState>()((set, get) => ({
  // Initial state
  currentView: 'month',
  currentDate: new Date(),
  showExternalEvents: true,
  showTasks: true,
  showScheduledTasks: true,
  isPlannerOpen: false,
  filters: { ...DEFAULT_FILTERS },

  // View actions
  setCurrentView: (view: CalendarView) => {
    set({ currentView: view });
  },

  setCurrentDate: (date: Date) => {
    set({ currentDate: date });
  },

  navigateToToday: () => {
    set({ currentDate: new Date() });
  },

  navigatePrevious: () => {
    const { currentDate, currentView } = get();
    set({ currentDate: navigateDate(currentDate, currentView, 'prev') });
  },

  navigateNext: () => {
    const { currentDate, currentView } = get();
    set({ currentDate: navigateDate(currentDate, currentView, 'next') });
  },

  // Display toggle actions
  toggleExternalEvents: () => {
    set((state) => ({ showExternalEvents: !state.showExternalEvents }));
  },

  toggleTasks: () => {
    set((state) => ({ showTasks: !state.showTasks }));
  },

  toggleScheduledTasks: () => {
    set((state) => ({ showScheduledTasks: !state.showScheduledTasks }));
  },

  setShowExternalEvents: (show: boolean) => {
    set({ showExternalEvents: show });
  },

  setShowTasks: (show: boolean) => {
    set({ showTasks: show });
  },

  setShowScheduledTasks: (show: boolean) => {
    set({ showScheduledTasks: show });
  },

  togglePlanner: () => {
    set((state) => ({ isPlannerOpen: !state.isPlannerOpen }));
  },

  setPlannerOpen: (open: boolean) => {
    set({ isPlannerOpen: open });
  },

  // Filter actions
  setFilters: (filters: Partial<CalendarFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  applyPreferences: (preferences: CalendarUserPreferences) => {
    set({
      filters: { ...DEFAULT_FILTERS, ...(preferences.filters ?? {}) },
      showTasks: preferences.showTasks ?? true,
      showScheduledTasks: preferences.showScheduledTasks ?? true,
      showExternalEvents: preferences.showExternalEvents ?? true,
    });
  },

  setFilter: <K extends keyof CalendarFilters>(
    key: K,
    value: CalendarFilters[K]
  ) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
  },

  resetFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS } });
  },

  clearFilter: (key: keyof CalendarFilters) => {
    set((state) => ({
      filters: { ...state.filters, [key]: [] },
    }));
  },
}));

// Selector hooks for specific state slices
export const useCalendarView = () =>
  useCalendarStore((state) => state.currentView);
export const useCalendarDate = () =>
  useCalendarStore((state) => state.currentDate);
export const useCalendarFilters = () =>
  useCalendarStore((state) => state.filters);
export const useShowExternalEvents = () =>
  useCalendarStore((state) => state.showExternalEvents);
export const useShowTasks = () => useCalendarStore((state) => state.showTasks);
export const useShowScheduledTasks = () =>
  useCalendarStore((state) => state.showScheduledTasks);
export const useIsPlannerOpen = () =>
  useCalendarStore((state) => state.isPlannerOpen);

// Check if any filters are active
export const useHasActiveFilters = () =>
  useCalendarStore((state) => {
    const { filters } = state;
    return (
      filters.entityIds.length > 0 ||
      filters.projectIds.length > 0 ||
      filters.assigneeIds.length > 0 ||
      filters.priorities.length > 0 ||
      filters.statuses.length > 0 ||
      filters.tagIds.length > 0
    );
  });
